import React, { useState, useEffect, useCallback} from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Textarea } from '../../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import { psAPI, controllingAPI } from '../../../services/api';
import { toast } from 'sonner';
import { withStableUid, attachStableUids, stripStableKeys } from '../../../hooks/useArrayWithStableKeys';
import { 
  Plus, Calculator, ChevronLeft, Edit2, CheckCircle, Clock,
  Archive, Trash2, Filter, Eye, Search, Building2, FileText,
  DollarSign, Target, TrendingUp
} from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';

const CostPlanning = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const writable = canWrite('cost_planning');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [projects, setProjects] = useState([]);
  const [wbsElements, setWbsElements] = useState([]);
  const [costElements, setCostElements] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    project_id: '',
    fiscal_year: new Date().getFullYear(),
    plan_status: ''
  });
  const [showDialog, setShowDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editPlan, setEditPlan] = useState(null);
  const [formData, setFormData] = useState({
    project_id: '',
    wbs_element_id: '',
    plan_version: '0',
    plan_category: 'COSTS',
    fiscal_year: new Date().getFullYear(),
    period_from: 1,
    period_to: 12,
    currency: 'USD',
    total_planned_amount: 0,
    planning_method: 'MANUAL',
    plan_status: 'DRAFT',
    description: '',
    line_items: []
  });
  const [lineItem, setLineItem] = useState({
    cost_element_id: '',
    amount: 0,
    quantity: null,
    unit: '',
    text: ''
  });


  useEffect(() => {
    fetchCostElements();
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [plansRes, projectsRes] = await Promise.all([
        psAPI.getCostPlans(filters),
        psAPI.getProjects()
      ]);
      setPlans(plansRes.data);
      setProjects(projectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load cost plans');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchCostElements = async () => {
    try {
      // Use real Controlling (CO) master data — active cost elements only.
      const response = await controllingAPI.getCostElements({ status: 'Active' });
      setCostElements(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to load cost elements from Controlling:', error);
      setCostElements([]);
    }
  };

  const fetchWBSElements = async (projectId) => {
    if (!projectId) return;
    try {
      const response = await psAPI.getWBSElements({ project_id: projectId });
      // Filter to planning elements only
      setWbsElements(response.data.filter(w => w.is_planning_element !== false));
    } catch (error) {
      console.error('Error fetching WBS elements:', error);
    }
  };

  const handleProjectChange = (projectId) => {
    setFormData(prev => ({ ...prev, project_id: projectId, wbs_element_id: '' }));
    fetchWBSElements(projectId);
  };

  const addLineItem = () => {
    if (!lineItem.cost_element_id || lineItem.amount <= 0) {
      toast.error('Please select cost element and enter amount');
      return;
    }

    const ce = costElements.find(c => c.cost_element_id === lineItem.cost_element_id);
    const newItem = withStableUid({
      ...lineItem,
      cost_element_name: ce?.name || lineItem.cost_element_id
    });

    const newTotal = formData.total_planned_amount + lineItem.amount;

    setFormData(prev => ({
      ...prev,
      line_items: [...prev.line_items, newItem],
      total_planned_amount: newTotal
    }));

    setLineItem({ cost_element_id: '', amount: 0, quantity: null, unit: '', text: '' });
  };

  const removeLineItem = (uid) => {
    setFormData(prev => {
      const item = prev.line_items.find(li => li._uid === uid);
      return {
        ...prev,
        line_items: prev.line_items.filter(li => li._uid !== uid),
        total_planned_amount: prev.total_planned_amount - (item?.amount || 0)
      };
    });
  };

  const handleSubmit = async () => {
    try {
      if (!formData.project_id || !formData.wbs_element_id) {
        toast.error('Please select project and WBS element');
        return;
      }

      const payload = {
        ...formData,
        line_items: stripStableKeys(formData.line_items, ['cost_element_name'])
      };

      if (editPlan) {
        await psAPI.updateCostPlan(editPlan.plan_id, payload);
        toast.success('Cost plan updated successfully');
      } else {
        await psAPI.createCostPlan(payload);
        toast.success('Cost plan created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving cost plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to save cost plan');
    }
  };

  const handleActivate = async (planId) => {
    try {
      await psAPI.activateCostPlan(planId);
      toast.success('Cost plan activated successfully');
      fetchData();
    } catch (error) {
      console.error('Error activating plan:', error);
      toast.error(error.response?.data?.detail || 'Failed to activate plan');
    }
  };

  const openEditDialog = (plan) => {
    setEditPlan(plan);
    setFormData({
      project_id: plan.project_id,
      wbs_element_id: plan.wbs_element_id,
      plan_version: plan.plan_version,
      plan_category: plan.plan_category,
      fiscal_year: plan.fiscal_year,
      period_from: plan.period_from,
      period_to: plan.period_to,
      currency: plan.currency,
      total_planned_amount: plan.total_planned_amount,
      planning_method: plan.planning_method,
      plan_status: plan.plan_status,
      description: plan.description || '',
      line_items: attachStableUids(plan.line_items || [])
    });
    fetchWBSElements(plan.project_id);
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditPlan(null);
    setFormData({
      project_id: '',
      wbs_element_id: '',
      plan_version: '0',
      plan_category: 'COSTS',
      fiscal_year: new Date().getFullYear(),
      period_from: 1,
      period_to: 12,
      currency: 'USD',
      total_planned_amount: 0,
      planning_method: 'MANUAL',
      plan_status: 'DRAFT',
      description: '',
      line_items: []
    });
    setWbsElements([]);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const config = {
      'DRAFT': { bg: 'bg-slate-100', text: 'text-slate-700', dot: 'bg-slate-400' },
      'ACTIVE': { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
      'LOCKED': { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
      'ARCHIVED': { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' }
    };
    const c = config[status] || config['DRAFT'];
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {status}
      </span>
    );
  };

  const openViewDialog = async (plan) => {
    try {
      const response = await psAPI.getCostPlan(plan.plan_id);
      setSelectedPlan(response.data);
      setShowViewDialog(true);
    } catch (error) {
      // Fallback to the list data if API doesn't exist
      setSelectedPlan(plan);
      setShowViewDialog(true);
    }
  };

  // Calculate stats
  const stats = {
    total: plans.length,
    totalAmount: plans.reduce((sum, p) => sum + (p.total_planned_amount || 0), 0),
    active: plans.filter(p => p.plan_status === 'ACTIVE').length,
    draft: plans.filter(p => p.plan_status === 'DRAFT').length
  };

  const filteredPlans = plans.filter(p =>
    (p.plan_id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (p.project_name?.toLowerCase() || p.project_id?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (p.wbs_element_name?.toLowerCase() || p.wbs_element_id?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
    <div className="p-6 space-y-6" data-testid="cost-planning-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/ps/planning')} className="hover:bg-slate-100">
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-600 rounded-lg">
              <Calculator className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Cost Planning</h1>
              <p className="text-sm text-slate-500">Plan costs by WBS element and cost element</p>
            </div>
          </div>
        </div>
        {writable && (
        <Button onClick={() => setShowDialog(true)} className="bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4 mr-2" />
          New Cost Plan
        </Button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-500">Total Plans</p>
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <FileText className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-green-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Planned</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(stats.totalAmount)}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-xl">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-emerald-600">Active Plans</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.active}</p>
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-50 to-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-600">Draft Plans</p>
                <p className="text-2xl font-bold text-slate-700">{stats.draft}</p>
              </div>
              <div className="p-3 bg-slate-100 rounded-xl">
                <Clock className="h-5 w-5 text-slate-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search by plan ID, project, or WBS..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-slate-200" />
            </div>
            <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({...filters, project_id: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[200px] bg-slate-50 border-slate-200">
                <Building2 className="h-4 w-4 mr-2 text-slate-400" />
                <SelectValue placeholder="All Projects" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Projects</SelectItem>
                {projects.map(p => <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filters.plan_status || "all"} onValueChange={(v) => setFilters({...filters, plan_status: v === "all" ? "" : v})}>
              <SelectTrigger className="w-[150px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="DRAFT">Draft</SelectItem>
                <SelectItem value="ACTIVE">Active</SelectItem>
                <SelectItem value="LOCKED">Locked</SelectItem>
                <SelectItem value="ARCHIVED">Archived</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(filters.fiscal_year)} onValueChange={(v) => setFilters({...filters, fiscal_year: parseInt(v)})}>
              <SelectTrigger className="w-[120px] bg-slate-50 border-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2024, 2025, 2026, 2027].map(y => <SelectItem key={y} value={String(y)}>FY {y}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border-0 shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="font-semibold text-slate-600">Plan ID</TableHead>
              <TableHead className="font-semibold text-slate-600">Project</TableHead>
              <TableHead className="font-semibold text-slate-600">WBS Element</TableHead>
              <TableHead className="font-semibold text-slate-600">Version</TableHead>
              <TableHead className="font-semibold text-slate-600">FY</TableHead>
              <TableHead className="font-semibold text-slate-600">Periods</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Amount</TableHead>
              <TableHead className="font-semibold text-slate-600">Status</TableHead>
              <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12">
                <div className="flex items-center justify-center gap-2 text-slate-500">
                  <div className="animate-spin h-5 w-5 border-2 border-green-600 border-t-transparent rounded-full"></div>
                  Loading...
                </div>
              </TableCell></TableRow>
            ) : filteredPlans.length === 0 ? (
              <TableRow><TableCell colSpan={9} className="text-center py-12">
                <div className="flex flex-col items-center gap-2">
                  <Calculator className="h-10 w-10 text-slate-300" />
                  <p className="text-slate-500">No cost plans found</p>
                </div>
              </TableCell></TableRow>
            ) : filteredPlans.map((plan) => (
              <TableRow key={plan.plan_id} className="group hover:bg-green-50/50 transition-colors">
                <TableCell>
                  <span className="font-mono text-sm font-medium text-green-600">{plan.plan_id}</span>
                </TableCell>
                <TableCell className="text-slate-600">{plan.project_name || plan.project_id}</TableCell>
                <TableCell className="text-slate-600">{plan.wbs_element_name || plan.wbs_element_id}</TableCell>
                <TableCell>
                  <Badge variant="outline" className="font-mono">v{plan.plan_version}</Badge>
                </TableCell>
                <TableCell className="text-slate-600">{plan.fiscal_year}</TableCell>
                <TableCell className="text-slate-600">{plan.period_from} - {plan.period_to}</TableCell>
                <TableCell className="text-right font-medium">{formatCurrency(plan.total_planned_amount)}</TableCell>
                <TableCell>{getStatusBadge(plan.plan_status)}</TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openViewDialog(plan)} title="View">
                      <Eye className="h-4 w-4 text-slate-600" />
                    </Button>
                    {writable && plan.plan_status === 'DRAFT' && (
                      <>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-slate-200" onClick={() => openEditDialog(plan)} title="Edit">
                          <Edit2 className="h-4 w-4 text-slate-600" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 opacity-60 group-hover:opacity-100 hover:bg-emerald-100" onClick={() => handleActivate(plan.plan_id)} title="Activate">
                          <CheckCircle className="h-4 w-4 text-emerald-600" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredPlans.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/50">
            <p className="text-sm text-slate-500">Showing <span className="font-medium text-slate-700">{filteredPlans.length}</span> plans</p>
          </div>
        )}
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editPlan ? 'Edit Cost Plan' : 'Create Cost Plan'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Header Fields */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Project *</Label>
                <Select 
                  value={formData.project_id || '__none__'} 
                  onValueChange={(v) => handleProjectChange(v === '__none__' ? '' : v)}
                  disabled={!!editPlan}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select project" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select project</SelectItem>
                    {projects.map(p => (
                      <SelectItem key={p.project_id} value={p.project_id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>WBS Element *</Label>
                <Select 
                  value={formData.wbs_element_id || '__none__'} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, wbs_element_id: v === '__none__' ? '' : v }))}
                  disabled={!formData.project_id || !!editPlan}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select WBS element" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Select WBS element</SelectItem>
                    {wbsElements.map(w => (
                      <SelectItem key={w.wbs_element_id} value={w.wbs_element_id}>{w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plan Version</Label>
                <Select 
                  value={formData.plan_version} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, plan_version: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">0 (Current)</SelectItem>
                    <SelectItem value="1">1 (Alternate)</SelectItem>
                    <SelectItem value="2">2 (Alternate)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fiscal Year *</Label>
                <Select 
                  value={formData.fiscal_year.toString()} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, fiscal_year: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(year => (
                      <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period From</Label>
                <Select 
                  value={formData.period_from.toString()} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, period_from: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>Period {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Period To</Label>
                <Select 
                  value={formData.period_to.toString()} 
                  onValueChange={(v) => setFormData(prev => ({ ...prev, period_to: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[...Array(12)].map((_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>Period {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Line Items by Cost Element */}
            <div>
              <Label className="text-base font-semibold">Plan Details by Cost Element</Label>
              <Card className="mt-2">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-5 gap-2 items-end mb-4">
                    <div>
                      <Label className="text-xs">Cost Element</Label>
                      <Select 
                        value={lineItem.cost_element_id || '__none__'} 
                        onValueChange={(v) => setLineItem(prev => ({ ...prev, cost_element_id: v === '__none__' ? '' : v }))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Select</SelectItem>
                          {costElements.length === 0 && (
                            <div className="px-3 py-2 text-xs text-gray-500" data-testid="cost-elements-empty">
                              No cost elements found. Create them in Controlling → Cost Elements.
                            </div>
                          )}
                          {costElements.map(ce => (
                            <SelectItem key={ce.cost_element_id} value={ce.cost_element_id}>
                              {ce.cost_element_id} - {ce.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Amount</Label>
                      <Input 
                        type="number"
                        className="h-9"
                        placeholder="0.00"
                        value={lineItem.amount || ''}
                        onChange={(e) => setLineItem(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Quantity</Label>
                      <Input 
                        type="number"
                        className="h-9"
                        placeholder="Qty"
                        value={lineItem.quantity || ''}
                        onChange={(e) => setLineItem(prev => ({ ...prev, quantity: parseFloat(e.target.value) || null }))}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Unit</Label>
                      <Input 
                        className="h-9"
                        placeholder="EA/HR"
                        value={lineItem.unit}
                        onChange={(e) => setLineItem(prev => ({ ...prev, unit: e.target.value }))}
                      />
                    </div>
                    <Button onClick={addLineItem} size="sm" className="h-9">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {formData.line_items.length > 0 && (
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">Cost Element</th>
                            <th className="px-3 py-2 text-right font-medium">Amount</th>
                            <th className="px-3 py-2 text-right font-medium">Qty</th>
                            <th className="px-3 py-2 text-left font-medium">Unit</th>
                            <th className="px-3 py-2 w-10"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.line_items.map((item) => (
                            <tr key={item._uid} className="border-t">
                              <td className="px-3 py-2">{item.cost_element_name || item.cost_element_id}</td>
                              <td className="px-3 py-2 text-right font-medium">{formatCurrency(item.amount)}</td>
                              <td className="px-3 py-2 text-right">{item.quantity || '-'}</td>
                              <td className="px-3 py-2">{item.unit || '-'}</td>
                              <td className="px-3 py-2">
                                <Button variant="ghost" size="sm" onClick={() => removeLineItem(item._uid)}>
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                          <tr className="border-t bg-gray-50">
                            <td className="px-3 py-2 font-semibold">Total Planned</td>
                            <td className="px-3 py-2 text-right font-bold text-orange-600">{formatCurrency(formData.total_planned_amount)}</td>
                            <td colSpan="3"></td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea 
                placeholder="Plan description..."
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-green-600 hover:bg-green-700">{editPlan ? 'Update' : 'Create'} Plan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-semibold">Cost Plan: {selectedPlan?.plan_id}</DialogTitle>
              {selectedPlan && getStatusBadge(selectedPlan.plan_status)}
            </div>
          </DialogHeader>
          {selectedPlan && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
                <div><p className="text-xs text-slate-500">Project</p><p className="font-medium">{selectedPlan.project_name || selectedPlan.project_id}</p></div>
                <div><p className="text-xs text-slate-500">WBS Element</p><p className="font-medium">{selectedPlan.wbs_element_name || selectedPlan.wbs_element_id}</p></div>
                <div><p className="text-xs text-slate-500">Version</p><p className="font-medium">v{selectedPlan.plan_version}</p></div>
                <div><p className="text-xs text-slate-500">Fiscal Year</p><p className="font-medium">{selectedPlan.fiscal_year}</p></div>
                <div><p className="text-xs text-slate-500">Periods</p><p className="font-medium">{selectedPlan.period_from} - {selectedPlan.period_to}</p></div>
                <div><p className="text-xs text-slate-500">Total Amount</p><p className="font-bold text-green-600">{formatCurrency(selectedPlan.total_planned_amount)}</p></div>
              </div>

              {/* Planning Method */}
              {selectedPlan.planning_method && (
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600 font-medium">Planning Method</p>
                  <p className="text-sm text-green-800">{selectedPlan.planning_method}</p>
                </div>
              )}

              {/* Description */}
              {selectedPlan.description && (
                <div className="p-4 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-600 font-medium">Description</p>
                  <p className="text-sm text-slate-700">{selectedPlan.description}</p>
                </div>
              )}

              {/* Line Items */}
              {selectedPlan.line_items?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-slate-700 mb-2">Cost Element Breakdown</p>
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead>Cost Element</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead>Unit</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPlan.line_items.map((item, index) => (
                        <TableRow key={item._uid || item.cost_element_id || `li-${index}`}>
                          <TableCell>{item.cost_element_name || item.cost_element_id}</TableCell>
                          <TableCell className="text-right font-medium">{formatCurrency(item.amount)}</TableCell>
                          <TableCell className="text-right">{item.quantity || '-'}</TableCell>
                          <TableCell>{item.unit || '-'}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-green-50">
                        <TableCell className="font-semibold">Total</TableCell>
                        <TableCell className="text-right font-bold text-green-600">{formatCurrency(selectedPlan.total_planned_amount)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            {writable && selectedPlan?.plan_status === 'DRAFT' && (
              <Button onClick={() => { setShowViewDialog(false); openEditDialog(selectedPlan); }} className="bg-green-600 hover:bg-green-700">
                <Edit2 className="h-4 w-4 mr-2" /> Edit Plan
              </Button>
            )}
            <Button variant="outline" onClick={() => setShowViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
};

export default CostPlanning;
